import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
    listCountries, listStates, reverseGeocode, requestGeolocation,
    setLocalLocation, patchMyLocation,
} from "@/lib/location";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, ArrowLeft, Loader2, Sparkles, Rocket } from "lucide-react";

const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? 300 : -300, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -300 : 300, opacity: 0 }),
};

export default function Onboarding() {
    const nav = useNavigate();
    const { user } = useAuth();
    const [step, setStep] = useState(1); // 1=country, 2=state, 3=district
    const [direction, setDirection] = useState(1);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [sel, setSel] = useState({ country_code: null, state_slug: null, district_slug: null });
    const [locating, setLocating] = useState(false);
    const [comingSoon, setComingSoon] = useState(null); // {country_name, state_name?}

    useEffect(() => { listCountries().then(setCountries).catch(() => {}); }, []);
    useEffect(() => {
        if (sel.country_code === "IN") listStates("IN").then(setStates).catch(() => {});
    }, [sel.country_code]);
    useEffect(() => {
        api.get("/districts").then((r) => setDistricts(r.data)).catch(() => {});
    }, []);

    const advance = (nextStep) => {
        setDirection(nextStep > step ? 1 : -1);
        setStep(nextStep);
    };

    const finish = async (loc) => {
        const value = setLocalLocation(loc);
        if (user) {
            try {
                await patchMyLocation({
                    country_code: loc.country_code,
                    state_slug: loc.state_slug,
                    district_slug: loc.district_slug,
                    source: loc.source || "MANUAL",
                });
            } catch { /* offline persist is fine */ }
        }
        toast.success(`Welcome to ${loc.district_name || loc.state_name || loc.country_name}!`);
        nav(`/home?district_slug=${loc.district_slug}`, { replace: true });
    };

    const useMyLocation = async () => {
        setLocating(true);
        try {
            const { lat, lng } = await requestGeolocation();
            const r = await reverseGeocode(lat, lng);
            if (r.supported && r.district_slug) {
                await finish({
                    country_code: r.country_code, country_name: r.country_name,
                    state_slug: r.state_slug, state_name: r.state_name,
                    district_slug: r.district_slug, district_name: r.district_name,
                    source: "GPS", lat, lng,
                });
                return;
            }
            setComingSoon({ country_name: r.country_name || "your area", state_name: r.state_name || null });
        } catch (e) {
            toast.error(errorMessage(e) || "Couldn't get your location. Pick manually below.");
        } finally { setLocating(false); }
    };

    const pickCountry = (c) => {
        if (!c.supported) return setComingSoon({ country_name: c.name });
        setSel({ country_code: c.code, state_slug: null, district_slug: null });
        advance(2);
    };
    const pickState = (s) => {
        if (!s.supported) return setComingSoon({ country_name: "India", state_name: s.name });
        setSel((v) => ({ ...v, state_slug: s.slug }));
        advance(3);
    };
    const pickDistrict = (d) => finish({
        country_code: "IN", country_name: "India",
        state_slug: "kerala", state_name: "Kerala",
        district_slug: d.slug, district_name: d.name,
        source: "MANUAL",
    });

    const heading = useMemo(() => {
        if (step === 1) return { title: "Where are you shopping today?", sub: "Pick your country to see local offers." };
        if (step === 2) return { title: "Which state?", sub: "We're rolling out across India. Kerala is live!" };
        return { title: "Pick your district", sub: "Kerala's 14 districts — hyperlocal deals near you." };
    }, [step]);

    return (
        <div className="min-h-screen overflow-hidden bg-white text-neutral-900" data-testid="onboarding">
            <header className="border-b border-orange-100 bg-[#FFF3E6] shadow-sm">
                <div className="mx-auto flex max-w-4xl items-center justify-center px-4 py-3">
                    <img src="/logo.png" alt="OfferNest" className="h-12" data-testid="onb-logo" />
                </div>
            </header>
            <div className="mx-auto flex min-h-[calc(100vh-64px)] max-w-4xl flex-col items-center px-4 py-10">
                <div className="text-center">
                    <h1 className="text-3xl font-black text-neutral-900 sm:text-4xl" data-testid="onb-heading">{heading.title}</h1>
                    <p className="mt-2 text-sm text-neutral-500">{heading.sub}</p>
                </div>

                <div className="relative mt-8 w-full">
                    <AnimatePresence mode="wait" custom={direction}>
                        {step === 1 && (
                            <motion.div key="s1" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35 }} className="grid gap-3 sm:grid-cols-3" data-testid="onb-country-grid">
                                {countries.map((c) => (
                                    <button key={c.code} onClick={() => pickCountry(c)} data-testid={`onb-country-${c.code}`}
                                        className="group flex flex-col items-center gap-2 rounded-2xl border-2 border-neutral-100 bg-white p-6 shadow-sm transition hover:border-orange-400 hover:shadow-md">
                                        <span className="text-5xl" aria-hidden>{c.flag}</span>
                                        <span className="text-lg font-bold text-neutral-900">{c.name}</span>
                                        {c.supported ? (
                                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Available</span>
                                        ) : (
                                            <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-semibold text-neutral-500">Coming soon</span>
                                        )}
                                    </button>
                                ))}
                            </motion.div>
                        )}
                        {step === 2 && (
                            <motion.div key="s2" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35 }} className="grid gap-3 sm:grid-cols-3" data-testid="onb-state-grid">
                                {states.map((s) => (
                                    <button key={s.slug} onClick={() => pickState(s)} data-testid={`onb-state-${s.slug}`}
                                        className="flex flex-col items-start gap-2 rounded-2xl border-2 border-neutral-100 bg-white p-6 shadow-sm transition hover:border-orange-400 hover:shadow-md">
                                        <span className="text-xl font-bold text-neutral-900">{s.name}</span>
                                        {s.supported ? (
                                            <span className="rounded-full bg-orange-500 px-2 py-0.5 text-[10px] font-bold text-white">Available</span>
                                        ) : (
                                            <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-[10px] font-bold text-neutral-600">Coming soon</span>
                                        )}
                                    </button>
                                ))}
                            </motion.div>
                        )}
                        {step === 3 && (
                            <motion.div key="s3" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35 }} className="grid gap-2 sm:grid-cols-3 lg:grid-cols-4" data-testid="onb-district-grid">
                                {districts.map((d, i) => (
                                    <button key={d.slug} onClick={() => pickDistrict(d)} data-testid={`onb-district-${d.slug}`}
                                        className="rounded-xl p-4 text-left font-semibold text-white shadow-sm transition hover:scale-105"
                                        style={{ background: `linear-gradient(135deg, hsl(${(i * 26) % 360} 70% 42%) 0%, hsl(${(i * 26 + 40) % 360} 65% 30%) 100%)` }}>
                                        <div className="text-lg leading-tight">{d.name}</div>
                                        <div className="mt-1 text-[10px] uppercase tracking-wide opacity-80">Kerala</div>
                                    </button>
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <div className="mt-6 flex flex-col items-center gap-3">
                    {step === 1 && (
                        <Button onClick={useMyLocation} disabled={locating} data-testid="onb-use-location" className="nest-gradient text-white">
                            {locating ? <Loader2 size={14} className="mr-2 animate-spin" /> : <MapPin size={14} className="mr-2" />}
                            Use my current location
                        </Button>
                    )}
                    {step > 1 && (
                        <button onClick={() => advance(step - 1)} data-testid="onb-back" className="inline-flex items-center gap-1 text-sm font-semibold text-neutral-500 hover:text-orange-600">
                            <ArrowLeft size={14} /> Back
                        </button>
                    )}
                </div>
            </div>

            <ComingSoonDialog open={!!comingSoon} onClose={() => setComingSoon(null)} location={comingSoon} />
        </div>
    );
}

function ComingSoonDialog({ open, onClose, location }) {
    const [email, setEmail] = useState("");
    const [sub, setSub] = useState(false);
    const [success, setSuccess] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setSub(true);
        try {
            const cc = location?.country_name === "India" ? "IN" : (location?.country_name === "UAE" ? "AE" : location?.country_name === "Qatar" ? "QA" : null);
            await api.post("/waitlist", { country_code: cc, state_slug: location?.state_name?.toLowerCase().replace(/\s+/g, "_") || null, contact_email: email });
            setSuccess(true);
        } catch (err) {
            toast.error(errorMessage(err));
        } finally { setSub(false); }
    };
    const close = () => { setEmail(""); setSuccess(false); onClose(); };
    const label = location?.state_name || location?.country_name || "your area";
    return (
        <Dialog open={open} onOpenChange={(v) => !v && close()}>
            <DialogContent className="max-w-md overflow-hidden bg-white p-0 text-center" data-testid="onb-coming-soon">
                <div className="bg-[#FFF3E6] p-4">
                    <img src="/coming-soon.png" alt="" className="mx-auto h-40 w-auto" data-testid="onb-cs-banner" />
                </div>
                <div className="p-5">
                    {success ? (
                        <>
                            <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600"><Sparkles /></div>
                            <DialogTitle className="text-center text-xl font-black" data-testid="onb-cs-success">You're on the list!</DialogTitle>
                            <p className="mt-2 text-sm text-neutral-600">We'll email you as soon as we launch in {label}.</p>
                            <button onClick={close} className="mt-4 text-xs font-semibold text-orange-600 hover:underline" data-testid="onb-cs-back">← Back to picker</button>
                        </>
                    ) : (
                        <>
                            <DialogHeader>
                                <DialogTitle className="text-center text-xl font-black">OfferNest is coming to {label} soon!</DialogTitle>
                            </DialogHeader>
                            <p className="text-sm text-neutral-600">We're expanding across India. Be the first to know when we launch here.</p>
                            <form onSubmit={submit} className="mt-4 flex gap-2">
                                <Input type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="onb-waitlist-email" />
                                <Button type="submit" disabled={sub || !email} className="nest-gradient text-white" data-testid="onb-waitlist-submit">
                                    <Sparkles size={14} className="mr-1" /> Notify me
                                </Button>
                            </form>
                            <button onClick={close} className="mt-3 text-xs text-neutral-500 hover:underline" data-testid="onb-coming-soon-close">Go back</button>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
